import { useCallback, useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, Cell,
} from "recharts";
import { GitBranch, AlertTriangle } from "lucide-react";
import { cockpitExperiments, cockpitExperiment, expAction } from "@/lib/rootConsole";

const PHASES = ["CANDIDATE", "SHADOW", "CANARY", "CHAMPION"];
const TERMINAL = { REJECTED: 1, ABORTED: 1, RETIRED: 1, ROLLED_BACK: 1 };
const num = (v) => (typeof v === "number" && isFinite(v) ? v : null);
const cell = (v) => (v == null ? <span className="nd">N/D</span> : (typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(2)) : String(v)));

const MET = [
  ["n", "Observations (N)"], ["mean_score", "Score VORTEX (↓ mieux)"],
  ["planned_km", "Km planifiés"], ["actual_km", "Km réels"],
  ["planned_min", "Durée prévue (min)"], ["actual_min", "Durée réelle (min)"],
  ["incidents", "Incidents"], ["missions", "Missions"], ["groupings", "Regroupements"],
  ["n_unknown", "UNKNOWN (exclus)"], ["n_exogenous_excluded", "Exogènes (exclus)"],
];

// EXPERIMENT PIPELINE — CANDIDATE→SHADOW→CANARY→CHAMPION. Impact Matrix + activation sûre.
// Réutilise les endpoints/gates existants (validation humaine obligatoire préservée).
export default function ExperimentPipeline({ selectedExpId, onSelectExp, refreshSignal }) {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const loadList = useCallback(async () => {
    try { setList((await cockpitExperiments()).experiments || []); }
    catch (e) { setErr(e?.response?.data?.detail || String(e)); }
  }, []);
  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try { setDetail(await cockpitExperiment(id)); } catch (e) { setErr(String(e)); }
  }, []);

  useEffect(() => { loadList(); }, [loadList, refreshSignal]);
  useEffect(() => { loadDetail(selectedExpId); setConfirm(false); }, [selectedExpId, loadDetail, refreshSignal]);

  const act = async (action, body = {}) => {
    if (!selectedExpId) return;
    setBusy(true); setErr(null);
    try { await expAction(selectedExpId, action, body); await loadDetail(selectedExpId); await loadList(); setConfirm(false); }
    catch (e) { setErr(`${action}: ${e?.response?.data?.detail || String(e)}`); }
    finally { setBusy(false); }
  };

  const exp = detail?.experiment;
  const im = detail?.impact;
  const phase = exp?.phase;
  const terminal = exp?.terminal;

  const chartData = im ? [
    { name: "Score", champion: num(im.champion?.mean_score), challenger: num(im.challenger?.mean_score), shadow: num(im.shadow?.mean_score) },
    { name: "Km réel", champion: num(im.champion?.actual_km), challenger: num(im.challenger?.actual_km), shadow: num(im.shadow?.actual_km) },
  ] : [];

  return (
    <section className="ck-panel" data-testid="experiment-pipeline">
      <div className="ck-panel__head">
        <h2><GitBranch size={12} style={{ verticalAlign: "-1px", marginRight: 6 }} />Experiment Pipeline</h2>
        <span className="sub">{list.length} expérimentation(s)</span>
      </div>
      <div className="ck-panel__body">
        {err && <div className="mix-err" data-testid="pipe-error">{err}</div>}

        {list.length === 0 && <div className="rail-empty" data-testid="pipe-empty">Aucune expérimentation. Bouge un fader puis « Proposer ».</div>}
        {list.slice(0, 8).map((e) => (
          <div key={e.experiment_id} className={"exp-row" + (e.experiment_id === selectedExpId ? " sel" : "")}
            onClick={() => onSelectExp(e.experiment_id)} data-testid={`exp-row-${e.experiment_id}`}>
            <div className="exp-row__t">
              <b>{e.param_key}</b>
              <span className={"exp-phase " + e.phase}>{e.phase}</span>
            </div>
            <div className="mono" style={{ fontSize: 10, color: "#778698", marginTop: 3 }}>
              {fmtV(e.champion_value)} → {fmtV(e.challenger_value)}{e.terminal_reason ? ` · ${e.terminal_reason}` : ""}
            </div>
          </div>
        ))}

        {exp && (
          <div style={{ marginTop: 14, borderTop: "1px solid #1c2836", paddingTop: 12 }} data-testid="exp-detail">
            <div className="pipe" data-testid="exp-pipe">
              {PHASES.map((ph, i) => {
                const idx = PHASES.indexOf(phase);
                const cls = TERMINAL[phase] ? "" : (ph === phase ? "on" : (i < idx ? "done" : ""));
                return (
                  <div key={ph} style={{ display: "contents" }}>
                    <div className={"pipe-step " + cls}>{ph}</div>
                    {i < PHASES.length - 1 && <span className="pipe-arrow">→</span>}
                  </div>
                );
              })}
            </div>
            {terminal && <div className="evidence-warn" data-testid="exp-terminal">ÉTAT TERMINAL : {phase} — {exp.terminal_reason}</div>}

            {im?.evidence === "INSUFFICIENT EVIDENCE" && (
              <div className="evidence-warn" data-testid="exp-insufficient">
                <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                INSUFFICIENT EVIDENCE — pas encore d&apos;observation challenger/shadow.
              </div>
            )}

            {im && (
              <>
                <table className="matrix" data-testid="impact-matrix">
                  <thead><tr><th>Métrique</th><th className="champ">CHAMPION</th><th className="chal">CHALLENGER</th><th>SHADOW</th><th>Δ abs</th><th>Δ rel</th></tr></thead>
                  <tbody>
                    {MET.map(([k, label]) => {
                      const d = im.deltas_challenger_vs_champion?.[k];
                      return (
                        <tr key={k}>
                          <td>{label}</td>
                          <td className="champ">{cell(im.champion?.[k])}</td>
                          <td className="chal">{cell(im.challenger?.[k])}</td>
                          <td>{cell(im.shadow?.[k])}</td>
                          <td>{d ? cell(d.absolute) : "—"}</td>
                          <td>{d && d.relative != null ? `${(d.relative * 100).toFixed(1)}%` : "—"}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="mono" style={{ fontSize: 10, color: "#778698" }}>
                  measurement_mode: {im.measurement_mode} · score plus bas = mieux
                </div>
                {chartData.some((d) => d.champion != null || d.challenger != null || d.shadow != null) && (
                  <div style={{ height: 150, marginTop: 8 }} data-testid="impact-chart">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData} margin={{ top: 6, right: 8, left: -18, bottom: 0 }}>
                        <XAxis dataKey="name" tick={{ fill: "#778698", fontSize: 10 }} axisLine={{ stroke: "#1c2836" }} tickLine={false} />
                        <YAxis tick={{ fill: "#778698", fontSize: 10 }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ background: "#0b1017", border: "1px solid #26374a", borderRadius: 8, fontSize: 12 }} cursor={{ fill: "rgba(167,139,250,0.06)" }} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="champion" fill="#34d399" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="challenger" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="shadow" fill="#5b9dff" radius={[3, 3, 0, 0]}><Cell /></Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            )}

            {!terminal && (
              <div className="act-row" data-testid="exp-actions">
                {phase === "CANDIDATE" && <button className="act-btn" disabled={busy} onClick={() => act("shadow")} data-testid="act-shadow">START SHADOW</button>}
                {phase === "SHADOW" && <button className="act-btn" disabled={busy} onClick={() => act("evaluate-shadow")} data-testid="act-eval-shadow">EVALUATE</button>}
                {phase === "CANARY" && <button className="act-btn" disabled={busy} onClick={() => act("evaluate-canary")} data-testid="act-eval-canary">EVALUATE</button>}
                {phase === "SHADOW" && <button className="act-btn promote" disabled={busy || !confirm} onClick={() => act("canary", { human_validated: true })} data-testid="act-canary">PROMOTE CANARY</button>}
                {phase === "CANARY" && <button className="act-btn promote" disabled={busy || !confirm} onClick={() => act("champion", { human_validated: true })} data-testid="act-champion">PROMOTE CHAMPION</button>}
                {phase === "CHAMPION" && <button className="act-btn danger" disabled={busy} onClick={() => act("rollback", { reason: "rollback Founder" })} data-testid="act-rollback">ROLLBACK</button>}
                <button className="act-btn danger" disabled={busy} onClick={() => act("abort", { reason: "abort Founder" })} data-testid="act-abort">ABORT</button>
              </div>
            )}
            {(phase === "SHADOW" || phase === "CANARY") && !terminal && (
              <label className="act-confirm" data-testid="exp-confirm">
                <input type="checkbox" checked={confirm} onChange={(e) => setConfirm(e.target.checked)} />
                Le Founder confirme la promotion (validation humaine obligatoire + audit).
              </label>
            )}

            {detail?.events?.length > 0 && (
              <div style={{ marginTop: 12 }} data-testid="exp-events">
                <div className="rail-title">Historique événements</div>
                {detail.events.slice(-6).map((ev, i) => (
                  <div key={i} className="mono" style={{ fontSize: 10, color: "#aab8c7", marginBottom: 3 }}>
                    {(ev.at || "").slice(0, 19).replace("T", " ")} · {ev.event}{ev.to_phase ? ` → ${ev.to_phase}` : ""} · {ev.actor}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}

const fmtV = (v) => (v == null ? "—" : (typeof v === "number" ? (Number.isInteger(v) ? v : v.toFixed(2)) : String(v)));
